import { formatCurrency } from "@/utils/formatCurrency";
import type { CartItem } from "@/types/cart.type";
import { Tag } from "lucide-react";
import { useState } from "react";
import DiscountModal from "./DiscountModal";

interface CartItemListProps {
  cart: CartItem[];
  onIncrease: (item: CartItem) => void;
  onDecrease: (item: CartItem) => void;
  onUpdateDiscount: (
    mtrl_code: string,
    discount: number, // selalu rupiah
  ) => void;
}

export default function CartItemList({
  cart,
  onIncrease,
  onDecrease,
  onUpdateDiscount,
}: CartItemListProps) {
  const [selectedItem, setSelectedItem] = useState<CartItem | null>(null);

  const handleOpenDiscount = (item: CartItem) => {
    setSelectedItem(item);
  };

  const handleSaveDiscount = (discountRupiah: number) => {
    if (selectedItem) {
      onUpdateDiscount(selectedItem.mtrl_code, discountRupiah);
      setSelectedItem(null);
    }
  };

  return (
    <>
      <div className="pb-105">
        {cart.map((item) => {
          const hasDiscount = item.discount > 0;
          const originalSubtotal = Number(item.sell_price) * item.qty;

          return (
            <div
              key={item.mtrl_code}
              className="bg-white dark:bg-[#37393d] mb-5 px-2 border-b border-gray-300 dark:border-gray-600"
            >
              <div className="px-4 py-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-[#1e1e1e] dark:text-white">
                      {item.mtrl_name}
                    </h3>
                    <p className="text-xs text-[#37393d]/90 mt-0.5 dark:text-white">
                      {item.cat_name}
                    </p>
                  </div>
                  <button
                    onClick={() => handleOpenDiscount(item)}
                    className="dark:text-white flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#d7d0fe]/30 hover:bg-[#d7d0fe]/50 transition text-xs font-medium text-[#37393d]"
                  >
                    <Tag className="w-3.5 h-3 " />
                    {hasDiscount ? "Edit" : "Diskon"}
                  </button>
                </div>
              </div>

              <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-gray-600 dark:text-white">
                    {formatCurrency(String(item.sell_price))} / {item.satuan}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-white">
                    Stock: {item.stock}
                  </div>
                </div>

                {hasDiscount && (
                  <div className="px-3 py-2 mb-3 border border-green-200 rounded-lg bg-green-50">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-green-700 dark:text-green-900">
                        Diskon: {formatCurrency(String(item.discount))}
                      </span>

                      <span className="text-green-600 dark:text-green-900">
                        Hemat{" "}
                        {formatCurrency(
                          String(originalSubtotal - item.subtotal),
                        )}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      className="w-8 h-8 rounded-lg bg-[#ff7075]/60 text-white flex items-center justify-center text-xl font-semibold hover:bg-[#ff7075]/80 transition"
                      onClick={() => onDecrease(item)}
                    >
                      −
                    </button>
                    <span className="w-10 text-lg font-semibold text-center dark:text-white">
                      {item.qty}
                    </span>
                    <button
                      className="flex items-center justify-center w-8 h-8 text-xl font-semibold text-white transition bg-green-500 rounded-lg hover:bg-green-600"
                      onClick={() => onIncrease(item)}
                    >
                      +
                    </button>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500 dark:text-white">
                      Subtotal
                    </div>
                    <div className="text-base font-semibold dark:text-white text-[#1e1e1e]">
                      {formatCurrency(String(item.subtotal))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <DiscountModal
        isOpen={selectedItem != null}
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onSave={handleSaveDiscount}
      />
    </>
  );
}
